import client, { request } from "Libs/platform";

import { matchConcurrencyRule } from "./concurrencyRules";
import { generateRequestKey, deduplicateRequest } from "./requestDeduplicator";

export type RequestMethods = "GET" | "POST" | "PATCH" | "DELETE";

const isAbsoluteUrl = (url: string) =>
  url.startsWith("http://") || url.startsWith("https://");

/**
 * Central request function with deduplication and concurrency limiting.
 *
 * Accepts both relative paths (prepends the configured api_url) and absolute
 * URLs (used as-is, e.g. HATEOAS links from the observability pipeline).
 *
 * GET requests are automatically deduplicated: if an identical GET is already
 * in-flight, the existing Promise is returned instead of making a second call.
 * Mutations (POST/PATCH/DELETE) always execute immediately.
 *
 * After dedup, the URL is matched against concurrency rules. If a rule matches,
 * the request is queued through a semaphore so that only N requests run in
 * parallel for that rule.
 *
 * Pipeline: dedup → concurrency limiter → fetch
 */
export const apiProxyRequest = <T = void>(
  url: string,
  method: RequestMethods = "GET",
  data?: object | FormData,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ...rest: any[]
) => {
  const fullUrl = isAbsoluteUrl(url)
    ? url
    : `${client.getConfig().api_url}${url}`;
  const key = generateRequestKey(method, fullUrl, data);

  return deduplicateRequest<T>(key, () => {
    const limiter = matchConcurrencyRule(fullUrl);
    const doRequest = () =>
      request(fullUrl, method, data, ...rest) as Promise<T>;
    return limiter ? limiter.run(doRequest) : doRequest();
  });
};

/**
 * A GET that refuses to share an in-flight response.
 *
 * `apiProxyRequest` hands a caller the Promise of an identical GET that is
 * already open. That is right when both callers want the same answer, and
 * wrong when the caller knows the resource just changed: it would resolve
 * with a response to a request issued before the change, and the store would
 * record pre-change data as current (CON-1655).
 *
 * Use it only for an explicit refresh. Everything else should dedup.
 */
export const apiProxyRequestFresh = <T = void>(url: string) => {
  const fullUrl = isAbsoluteUrl(url)
    ? url
    : `${client.getConfig().api_url}${url}`;

  return deduplicateRequest<T>(
    generateRequestKey("GET", fullUrl),
    () => {
      const limiter = matchConcurrencyRule(fullUrl);
      const doRequest = () => request(fullUrl, "GET") as Promise<T>;
      return limiter ? limiter.run(doRequest) : doRequest();
    },
    { skipDeduplication: true }
  );
};
